import { Flame, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { UserAvatar } from "@/components/common/UserAvatar";
import { cn } from "@/lib/utils";
import { hairlineClass, identityRingClass, identityWashClass } from "@/lib/card-styles";
import { typeCaptionClass, typeSubtitleClass } from "@/lib/typography";

export interface ProfileHeaderProps {
  userId: string | null;
  displayName: string;
  email: string;
  avatarUrl: string | null;
  currentStreak: number;
  level: number;
  className?: string;
}

/**
 * Who you are, at the top of the profile modal.
 *
 * ## It used to live in the desktop rail only
 *
 * The rail carried the avatar, name, email, and three stat chips; mobile
 * re-implemented the whole block a second time at a smaller size. Two copies of
 * an identity header drift — which is why the mobile one had drifted already.
 * One component now renders once, above both layouts, so a rail that is purely
 * navigation sits beside content and not another avatar.
 *
 * ## The chips are quiet on purpose
 *
 * Streak and level answer "am I keeping this up" without being the reason to
 * open the dialog — the dialog is for *changing* something. They sit under the
 * email rather than competing with the name for the first line.
 *
 * ## The name is serif
 *
 * The type scale's one rule: a string a learner reads as subject matter gets
 * Fraunces. A person's own name in their own settings is the most subject-like
 * string in the product, and the serif + gradient ring + warm wash is what
 * stops this header reading as the top of a settings table. The wash is
 * `identityWashClass` — `PageHero`'s stone→paper→amber background, deliberately
 * the opposite end of the ramp from `primaryCtaClass`'s button fill.
 */
export function ProfileHeader({
  userId,
  displayName,
  email,
  avatarUrl,
  currentStreak,
  level,
  className,
}: ProfileHeaderProps) {
  const { t } = useTranslation();
  const name = displayName || email || t("settings.profile.fallbackName", "Learner");
  const hasStreak = currentStreak > 0;

  return (
    <div
      className={cn(
        "relative flex shrink-0 items-center gap-4 overflow-hidden border-b px-5 py-5 sm:px-6",
        hairlineClass,
        identityWashClass,
        className,
      )}
    >
      <UserAvatar
        userId={userId}
        displayName={displayName}
        email={email}
        avatarUrl={avatarUrl}
        size="xl"
        className={cn(
          "h-16 w-16 shrink-0 shadow-md sm:h-20 sm:w-20",
          identityRingClass,
        )}
        fallbackClassName="bg-primary-strong text-white font-semibold text-2xl"
      />

      <div className="min-w-0 flex-1">
        <h2
          className={cn(
            "truncate font-display text-xl font-semibold tracking-tight sm:text-2xl",
            typeSubtitleClass,
          )}
        >
          {name}
        </h2>
        <p className={cn("mt-0.5 truncate text-xs sm:text-sm", typeCaptionClass)}>{email}</p>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
              hasStreak
                ? "bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 dark:from-amber-900/60 dark:to-orange-900/50 dark:text-amber-300"
                : "bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400",
            )}
          >
            <Flame className="h-3.5 w-3.5" aria-hidden="true" />
            {hasStreak
              ? t("nav.avatar.streak.active", {
                  defaultValue: "{{count}} day streak",
                  count: currentStreak,
                })
              : t("nav.avatar.streak.idle", "Start a streak today")}
          </span>

          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            {t("nav.avatar.level", { defaultValue: "Lv. {{level}}", level })}
          </span>
        </div>
      </div>
    </div>
  );
}

export default ProfileHeader;